import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, XIcon, ChevronDownIcon } from 'lucide-react';
type OptionItem = string | {
  value: string;
  label: string;
};
interface SearchableSelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: OptionItem[];
  placeholder?: string;
  className?: string;
  maxVisible?: number;
}
function getOptionValue(o: OptionItem) {
  return typeof o === 'string' ? o : o.value;
}
function getOptionLabel(o: OptionItem) {
  return typeof o === 'string' ? o : o.label;
}
export function SearchableSelect({
  label,
  value,
  onChange,
  options,
  placeholder = 'Buscar...',
  className = '',
  maxVisible = 5
}: SearchableSelectProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);
  const allFiltered = query.trim().length === 0 ? options : options.filter(o => getOptionLabel(o).toLowerCase().includes(query.toLowerCase()));
  const visible = allFiltered.slice(0, maxVisible);
  const totalFiltered = allFiltered.length;
  const selectedLabel = options.find(o => getOptionValue(o) === value) ? getOptionLabel(options.find(o => getOptionValue(o) === value)!) : '';
  const handleSelect = (option: OptionItem) => {
    onChange(getOptionValue(option));
    setIsOpen(false);
    setQuery('');
  };
  const handleOpen = () => {
    setIsOpen(true);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 50);
  };
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
    setIsOpen(false);
  };
  return <div className={`relative ${className}`} ref={containerRef} data-id="element-242">
      {label && <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-243">
          {label}
        </label>}

      {/* Trigger button */}
      {!isOpen ? <button type="button" onClick={handleOpen} className="w-full flex items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400 transition-colors" data-id="element-244">
          <span className={value && selectedLabel ? 'text-gray-900 truncate' : 'text-gray-400'} data-id="element-245">
            {selectedLabel || placeholder}
          </span>
          <div className="flex items-center gap-1 flex-shrink-0" data-id="element-246">
            {value && <span onClick={handleClear} className="p-0.5 text-gray-400 hover:text-gray-600 rounded cursor-pointer" data-id="element-247">
                <XIcon className="w-3.5 h-3.5" data-id="element-248" />
              </span>}
            <ChevronDownIcon className="w-4 h-4 text-gray-400" data-id="element-249" />
          </div>
        </button> : <div className="relative" data-id="element-250">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-251" />
          <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Escribir para buscar..." className="w-full pl-9 pr-4 py-2.5 text-sm border border-blue-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" data-id="element-252" />
        </div>}

      {/* Dropdown */}
      {isOpen && <div className="absolute z-[9999] top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden" data-id="element-253">
          {visible.length === 0 ? <div className="px-4 py-3 text-sm text-gray-400 text-center" data-id="element-254">
              No se encontraron resultados
            </div> : <>
              {visible.map(option => {
          const val = getOptionValue(option);
          const lbl = getOptionLabel(option);
          return <button key={val} type="button" onMouseDown={() => handleSelect(option)} className={`w-full text-left px-4 py-2.5 text-sm border-b border-gray-50 last:border-0 transition-colors ${val === value ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-255">
                    {lbl}
                  </button>;
        })}
              <div className="px-4 py-2 text-xs text-gray-400 bg-gray-50 border-t border-gray-100" data-id="element-256">
                {query.trim().length === 0 ? `Mostrando ${Math.min(maxVisible, options.length)} de ${options.length} — escribe para buscar` : totalFiltered > maxVisible ? `Mostrando ${maxVisible} de ${totalFiltered} resultados` : `${totalFiltered} resultado${totalFiltered !== 1 ? 's' : ''}`}
              </div>
            </>}
        </div>}
    </div>;
}